import type { Role } from '../data/types.js';

/**
 * Responsible parties (owner rulings 2026-10-18, docs/dashboards.md): a
 * template can require audits from the DISTRICT'S DM every N days, from
 * the AUDITOR GROUP every M days, or both. Each requirement is its own
 * compliance clock per store (a StoreAuditStatus row with Party), so
 * "who isn't doing them" is never ambiguous:
 *  - a DM's audit resets the DM clock; an Auditor's (or Admin's) resets
 *    the Auditor clock — separate clocks, never both;
 *  - a template with ONE DM/Auditor requirement is satisfied by whoever
 *    conducts it (today's DM-only facility audit keeps working for
 *    auditors too).
 * STORE TEAM (owner rulings 2026-10-19): a third party — the store's own
 * team every K days (a self-audit). Only store teams start a template whose
 * only requirement is theirs (Admin may, for testing); a store team can
 * only start templates that require the store team, and its audit resets
 * only the store clock. Reminders go to the store account, escalations to
 * the DM; lateness counts in the grade, the store's own SCORE doesn't.
 * LONG DISTANCE (owner rulings 2026-10-20, docs/dm-visit-mapping.md): a
 * store flagged Long distance (flight + hotel) uses each template's SECOND
 * window for the DM and Auditor clocks when the template sets one (blank =
 * the normal window); store-team clocks never change (always on site).
 * Pure; the flows mirror it (docs/flows.md).
 */

export type Party = 'dm' | 'auditor' | 'store';

export const PARTIES: readonly Party[] = ['dm', 'auditor', 'store'];

export const PARTY_LABEL: Record<Party, string> = { dm: 'DM', auditor: 'Auditor', store: 'Store team' };
/** Which schedule an audit counted toward, as a report column says it (UI review U107: never "Whose clock"). */
export const PARTY_AUDIT_LABEL: Record<Party, string> = { dm: 'DM audit', auditor: 'Auditor audit', store: 'Store self-audit' };

export interface Requirement {
  party: Party;
  /** Days between audits for this party. */
  frequencyDays: number;
  /** Long-distance stores' window (DM/Auditor only); absent/null = frequencyDays. */
  longDistanceDays?: number | null;
}

export interface RequirementSettings {
  /** The DM requirement's window (the template's long-standing FrequencyDays). */
  frequencyDays: number | null;
  /** false = no DM requirement (an auditor-only template). */
  requireDm: boolean;
  /** The Auditor requirement's window; null = no Auditor requirement. */
  auditorFrequencyDays: number | null;
  /** The store team's window; null/absent = no store requirement. */
  storeFrequencyDays?: number | null;
  /** The DM window for long-distance stores; null/absent = the normal one. */
  longDistanceFrequencyDays?: number | null;
  /** The Auditor window for long-distance stores; null/absent = the normal one. */
  auditorLongDistanceFrequencyDays?: number | null;
}

/**
 * The responsible-party columns of an AuditTemplates row (any source:
 * Graph fields in the app or on the server), defaults applied.
 * DmNotRequired is inverted on purpose (blank/No = the DM is required).
 */
export function requirementSettingsOf(f: Record<string, unknown>): RequirementSettings {
  const num = (v: unknown) => (typeof v === 'number' ? v : null);
  return {
    frequencyDays: num(f.FrequencyDays),
    requireDm: f.DmNotRequired !== true,
    auditorFrequencyDays: num(f.AuditorFrequencyDays),
    storeFrequencyDays: num(f.StoreFrequencyDays),
    longDistanceFrequencyDays: num(f.LongDistanceFrequencyDays),
    auditorLongDistanceFrequencyDays: num(f.AuditorLongDistanceFrequencyDays),
  };
}

/** Longest window accepted (10 years): a typo like 900000000 would break the date math (security review 2026-10-20). */
export const MAX_WINDOW_DAYS = 3650;

const validDays = (n: number | null): n is number =>
  n !== null && Number.isInteger(n) && n >= 1 && n <= MAX_WINDOW_DAYS;

/** The template's requirements: DM, Auditor, Store team. Invalid/blank windows are no requirement. */
export function requirementsOf(t: RequirementSettings): Requirement[] {
  const out: Requirement[] = [];
  // Only a valid long-distance window is carried (requirementErrors blocks publishing a bad one).
  const ld = (n: number | null | undefined) => (validDays(n ?? null) ? { longDistanceDays: n as number } : {});
  if (t.requireDm && validDays(t.frequencyDays)) {
    out.push({ party: 'dm', frequencyDays: t.frequencyDays, ...ld(t.longDistanceFrequencyDays) });
  }
  if (validDays(t.auditorFrequencyDays)) {
    out.push({ party: 'auditor', frequencyDays: t.auditorFrequencyDays, ...ld(t.auditorLongDistanceFrequencyDays) });
  }
  const storeDays = t.storeFrequencyDays ?? null;
  if (validDays(storeDays)) {
    out.push({ party: 'store', frequencyDays: storeDays });
  }
  return out;
}

/**
 * The window this requirement uses at a store: the long-distance one for a
 * Long-distance store when the template sets it, else the normal one.
 */
export function windowDays(req: Requirement, longDistance: boolean): number {
  return longDistance && req.longDistanceDays ? req.longDistanceDays : req.frequencyDays;
}

/** Which party a conductor's role belongs to: DMs are DMs; Auditors and Admins audit as Auditors; store teams as the store. */
export function partyForRole(role: Role | undefined): Party {
  if (role === 'StoreTeam') return 'store';
  return role === 'Auditor' || role === 'Admin' ? 'auditor' : 'dm';
}

/**
 * The requirement an audit counts toward: the one matching the conductor's
 * party, else — for DMs/Auditors/Admins — the template's only DM/Auditor
 * requirement. A store team only ever resets the store clock. Admin may
 * stand in for the store on a store-only template (testing). null = the
 * template has none for this person (it still records and scores; it
 * resets no clock).
 */
export function creditedRequirement(
  requirements: readonly Requirement[],
  conductorRole: Role | undefined,
): Requirement | null {
  const party = partyForRole(conductorRole);
  const own = requirements.find((r) => r.party === party);
  if (own) return own;
  if (party === 'store') return null;
  const staff = requirements.filter((r) => r.party !== 'store');
  if (staff.length === 1) return staff[0]!;
  if (staff.length === 0 && requirements.length === 1 && conductorRole === 'Admin') return requirements[0]!;
  return null;
}

/**
 * Who may START an audit of a template (the server checks the same rule):
 * store teams only templates that require the store team; DMs and
 * Auditors templates with a DM or Auditor requirement; Admin anything.
 */
export function canStartTemplate(role: Role, requirements: readonly Requirement[]): boolean {
  if (role === 'Admin') return true;
  if (role === 'StoreTeam') return requirements.some((r) => r.party === 'store');
  if (role === 'DM' || role === 'Auditor') return requirements.some((r) => r.party !== 'store');
  return false;
}

/** A StoreAuditStatus (or Audits) row's party: blank (every row before 2026-10-18) = the DM clock. */
export function partyOf(raw: unknown): Party {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return v === 'auditor' ? 'auditor' : v === 'store' ? 'store' : 'dm';
}

/** Settings problems (shown in the template settings dialog and blocking publish). */
export function requirementErrors(t: RequirementSettings): string[] {
  const errors: string[] = [];
  if (t.requireDm && !validDays(t.frequencyDays)) {
    errors.push('"DM: audit every (days)" must be a whole number from 1 to 3650.');
  }
  if (t.auditorFrequencyDays !== null && !validDays(t.auditorFrequencyDays)) {
    errors.push('"Auditor: audit every (days)" must be a whole number from 1 to 3650.');
  }
  const storeDays = t.storeFrequencyDays ?? null;
  if (storeDays !== null && !validDays(storeDays)) {
    errors.push('"Store team: audit every (days)" must be a whole number from 1 to 3650.');
  }
  const dmLd = t.longDistanceFrequencyDays ?? null;
  if (t.requireDm && dmLd !== null && !validDays(dmLd)) {
    errors.push('"DM: long-distance stores every (days)" must be a whole number from 1 to 3650, or blank.');
  }
  const auditorLd = t.auditorLongDistanceFrequencyDays ?? null;
  if (t.auditorFrequencyDays !== null && auditorLd !== null && !validDays(auditorLd)) {
    errors.push('"Auditor: long-distance stores every (days)" must be a whole number from 1 to 3650, or blank.');
  }
  if (!t.requireDm && t.auditorFrequencyDays === null && storeDays === null) {
    errors.push('Choose who must do this audit: the DM, an Auditor, the store team, or a mix.');
  }
  return errors;
}
